// Extended service page content — kept separate so translations.js stays lean.
// Each service id maps to page-level copy + gallery images in EN + IT.

const IMG = {
  brandingA: "https://images.pexels.com/photos/9121155/pexels-photo-9121155.jpeg?auto=compress&cs=tinysrgb&w=1600",
  brandingB: "https://images.pexels.com/photos/34221129/pexels-photo-34221129.jpeg?auto=compress&cs=tinysrgb&w=1600",
  brandingC: "https://images.unsplash.com/photo-1637250096679-c10f2751def8?auto=format&fit=crop&w=1600&q=80",
  brandingD: "https://images.pexels.com/photos/37793325/pexels-photo-37793325.jpeg?auto=compress&cs=tinysrgb&w=1600",

  digitalA: "https://images.unsplash.com/photo-1612544409025-e1f6a56c1152?auto=format&fit=crop&w=1600&q=80",
  digitalB: "https://images.pexels.com/photos/9121155/pexels-photo-9121155.jpeg?auto=compress&cs=tinysrgb&w=1600",
  digitalC: "https://images.pexels.com/photos/34221129/pexels-photo-34221129.jpeg?auto=compress&cs=tinysrgb&w=1600",
  digitalD: "https://images.unsplash.com/photo-1637250096679-c10f2751def8?auto=format&fit=crop&w=1600&q=80",

  interiorA: "https://images.pexels.com/photos/37793325/pexels-photo-37793325.jpeg?auto=compress&cs=tinysrgb&w=1600",
  interiorB: "https://images.unsplash.com/photo-1783700085825-1df197a49f40?auto=format&fit=crop&w=1600&q=80",
  interiorC: "https://images.pexels.com/photos/9121155/pexels-photo-9121155.jpeg?auto=compress&cs=tinysrgb&w=1600",
  interiorD: "https://images.unsplash.com/photo-1637250096679-c10f2751def8?auto=format&fit=crop&w=1600&q=80",

  fashionA: "https://images.pexels.com/photos/34221129/pexels-photo-34221129.jpeg?auto=compress&cs=tinysrgb&w=1600",
  fashionB: "https://images.pexels.com/photos/9121155/pexels-photo-9121155.jpeg?auto=compress&cs=tinysrgb&w=1600",
  fashionC: "https://images.unsplash.com/photo-1637250096679-c10f2751def8?auto=format&fit=crop&w=1600&q=80",
  fashionD: "https://images.pexels.com/photos/37793325/pexels-photo-37793325.jpeg?auto=compress&cs=tinysrgb&w=1600",

  musicA: "https://images.unsplash.com/photo-1612544409025-e1f6a56c1152?auto=format&fit=crop&w=1600&q=80",
  musicB: "https://images.unsplash.com/photo-1637250096679-c10f2751def8?auto=format&fit=crop&w=1600&q=80",
  musicC: "https://images.pexels.com/photos/34221129/pexels-photo-34221129.jpeg?auto=compress&cs=tinysrgb&w=1600",
  musicD: "https://images.pexels.com/photos/9121155/pexels-photo-9121155.jpeg?auto=compress&cs=tinysrgb&w=1600",

  marketingA: "https://images.unsplash.com/photo-1637250096679-c10f2751def8?auto=format&fit=crop&w=1600&q=80",
  marketingB: "https://images.pexels.com/photos/34221129/pexels-photo-34221129.jpeg?auto=compress&cs=tinysrgb&w=1600",
  marketingC: "https://images.pexels.com/photos/37793325/pexels-photo-37793325.jpeg?auto=compress&cs=tinysrgb&w=1600",
  marketingD: "https://images.unsplash.com/photo-1612544409025-e1f6a56c1152?auto=format&fit=crop&w=1600&q=80",

  graphicA: "https://images.pexels.com/photos/9121155/pexels-photo-9121155.jpeg?auto=compress&cs=tinysrgb&w=1600",
  graphicB: "https://images.pexels.com/photos/34221129/pexels-photo-34221129.jpeg?auto=compress&cs=tinysrgb&w=1600",
  graphicC: "https://images.unsplash.com/photo-1783700085825-1df197a49f40?auto=format&fit=crop&w=1600&q=80",
  graphicD: "https://images.unsplash.com/photo-1637250096679-c10f2751def8?auto=format&fit=crop&w=1600&q=80",
};

export const servicePages = {
  en: {
    branding: {
      number: "01",
      name: "Branding",
      tagline: "Systems that make brands\nunforgettable.",
      intro:
        "A brand is not a logo. It is a system — a set of decisions, made in advance, that let every touchpoint speak with the same voice. We design those systems from the ground up: strategic positioning, visual and verbal identity, and the playbooks your team needs to keep it alive after we hand over the keys.",
      paragraphs: [
        "We start with the strategy layer — the why. Who is this brand for, what does it stand against, and what promise does it make? From there we build the identity: wordmarks, symbols, type systems, color, motion, sound, tone of voice. Everything designed to compose, not just co-exist.",
        "Deliverables are documented in brand books that your team can actually use — no 200-page PDFs no one opens. Instead: a living site, componentized assets, and a launch playbook that ships with velocity.",
      ],
      capabilities: [
        "Brand strategy & positioning",
        "Naming & verbal identity",
        "Logo, wordmark & symbol systems",
        "Typography & color systems",
        "Brand guidelines / brand hubs",
        "Launch strategy & rollout",
      ],
      approach: [
        {
          n: "01",
          title: "Diagnose",
          body: "Workshops, audits, competitive teardowns. We map where the brand actually sits vs. where it needs to move.",
        },
        {
          n: "02",
          title: "Design",
          body: "Two directions, iterated. We test extremes so the final system is chosen — not defaulted into.",
        },
        {
          n: "03",
          title: "Deploy",
          body: "Guidelines, templates, and a launch plan. We stay in the room for the first six weeks post-launch.",
        },
      ],
      gallery: [IMG.brandingA, IMG.brandingB, IMG.brandingC, IMG.brandingD],
      quote:
        "The brands we admire aren't loud — they're coherent. Every surface confirms the last.",
    },
    digital: {
      number: "02",
      name: "Digital Content",
      tagline: "Content that earns\nthe scroll.",
      intro:
        "Digital content is where brand becomes culture. We conceive, produce and direct campaigns, editorial series, film and photography for the feeds your audience actually lives in — with art direction that survives being reposted, cropped and re-shared.",
      paragraphs: [
        "From single-image drops to multi-market campaigns, we design content systems that are both distinct and repeatable. Cast, styling, motion, sound, edit — every layer directed with the same care given to a print campaign.",
        "We build in-house content engines for brands that need to ship weekly, and we partner with clients who want to raise the bar on their flagship moments.",
      ],
      capabilities: [
        "Campaign creative direction",
        "Editorial content series",
        "Film & video production",
        "Fashion & product photography",
        "Social art direction & content systems",
        "Casting & production",
      ],
      approach: [
        { n: "01", title: "Idea", body: "One concept, sharp enough to survive translation across formats and markets." },
        { n: "02", title: "Craft", body: "Cast, location, light, sound. We treat the details that others skip." },
        { n: "03", title: "Ship", body: "Weekly cadence, monthly reviews, quarterly resets. Content as a system, not a stunt." },
      ],
      gallery: [IMG.digitalA, IMG.digitalB, IMG.digitalC, IMG.digitalD],
      quote:
        "The best content doesn't chase trends. It sets the terms of the next one.",
    },
    interior: {
      number: "03",
      name: "Interior Design",
      tagline: "Spaces that\nsay everything\nbefore you do.",
      intro:
        "We design interiors for retail flagships, private residences and hospitality — spaces where architecture, brand, and behavior become the same thing. Our approach is material-first and program-driven: we start with how a room should feel, then we design what makes that feeling inevitable.",
      paragraphs: [
        "Every project starts on-site. Light, sightlines, adjacencies. Then we work bottom-up: material palette, custom joinery, lighting design, wayfinding, brand integration. The result is spatial storytelling that scales — from a single flagship to a global rollout.",
        "We collaborate with local fabricators, artists, and specialists in every city we build in — because the best interiors carry a trace of where they were made.",
      ],
      capabilities: [
        "Retail interiors & flagship stores",
        "Private residential architecture",
        "F&B and hospitality design",
        "Furniture & custom joinery",
        "Lighting design & material palettes",
        "Rollout & construction supervision",
      ],
      approach: [
        { n: "01", title: "Site", body: "Every project begins by listening to the space — before drawings, before mood." },
        { n: "02", title: "Story", body: "A narrative brief the space needs to tell — from entrance to exit." },
        { n: "03", title: "Build", body: "Prototypes, samples, mock-ups. We favour materials over renders." },
      ],
      gallery: [IMG.interiorA, IMG.interiorB, IMG.interiorC, IMG.interiorD],
      quote:
        "Architecture is silent branding. It works on your body before it works on your mind.",
    },
    fashion: {
      number: "04",
      name: "Fashion",
      tagline: "Direction that turns\ngarments into\nlanguage.",
      intro:
        "Fashion sits at the center of what we do. We direct collections, build seasonal narratives, produce lookbooks, and partner with houses on the collaborations that define their year. Our team includes stylists, image-makers and brand strategists who work as one — because a collection isn't just clothes, it's a point of view.",
      paragraphs: [
        "From established maisons to emerging labels, we design collection direction, seasonal concepts, and the campaigns and lookbooks that carry them into the world. Deliberate casting. Signature styling. Films that are as considered as the garments they photograph.",
        "We also lead brand collaborations: the trickier the pairing, the more we like it. Multi-brand universes done with taste.",
      ],
      capabilities: [
        "Collection direction & seasonal briefs",
        "Lookbooks & campaign imagery",
        "Styling & art direction",
        "Fashion film",
        "Brand collaborations",
        "Show & presentation design",
      ],
      approach: [
        { n: "01", title: "Mood", body: "Reference walls, film references, cultural triangulation. Where is the season standing?" },
        { n: "02", title: "Cast", body: "Casting is direction. We hunt across cities and communities, not agencies alone." },
        { n: "03", title: "Frame", body: "Photography and film that hold up next season — not just this one." },
      ],
      gallery: [IMG.fashionA, IMG.fashionB, IMG.fashionC, IMG.fashionD],
      quote:
        "A great collection tells you where the culture is going, six months before it gets there.",
    },
    music: {
      number: "05",
      name: "Music",
      tagline: "Sound that\ncarries the brand.",
      intro:
        "Music is our secret weapon. We build sonic identities from scratch, score original tracks for films and campaigns, supervise music for brand and retail environments, and work directly with artists as creative directors. Sound is the fastest way to make a brand feel — we take it seriously.",
      paragraphs: [
        "In-house composers, mixers and music supervisors work alongside our design team from day one — so the sonic identity is not an afterthought bolted onto the visual system, but a native part of it.",
        "For artists, we act as extended creative direction: cover art, campaign, film, live show. One studio, one language.",
      ],
      capabilities: [
        "Sonic identity systems",
        "Original scoring for film & campaign",
        "Music supervision & licensing",
        "Retail & environmental sound design",
        "Artist creative direction",
        "Album & tour design",
      ],
      approach: [
        { n: "01", title: "Listen", body: "We map the sonic territory the brand occupies — and the one it should." },
        { n: "02", title: "Compose", body: "Original work from our in-house composers, with the option to license." },
        { n: "03", title: "Play", body: "Deployment across every touchpoint: from film to store to hold music." },
      ],
      gallery: [IMG.musicA, IMG.musicB, IMG.musicC, IMG.musicD],
      quote:
        "You forget what a brand said. You never forget how it sounded.",
    },
    marketing: {
      number: "06",
      name: "Marketing",
      tagline: "Growth without\nlosing the plot.",
      intro:
        "We help brands go to market without losing what makes them worth marketing in the first place. Strategy, launches, paid media, growth systems, retention — always in the service of the brand's long-term equity, never at its expense.",
      paragraphs: [
        "Our marketing practice is embedded with the creative team from day one. That means the ad, the landing page, the CRM email and the retail moment all share the same DNA — because they were all designed at the same table.",
        "We build growth systems that don't require the founder to become a full-time marketer. Cadenced launches, measurable channels, and the discipline to say no to the tactics that will hurt the brand next year.",
      ],
      capabilities: [
        "Go-to-market strategy",
        "Launch playbooks",
        "Paid media & performance",
        "CRM & lifecycle marketing",
        "Growth experimentation",
        "Retention & community",
      ],
      approach: [
        { n: "01", title: "Position", body: "What are you selling, to whom, against whom? The rest is downstream." },
        { n: "02", title: "Prove", body: "Small, real experiments — before we scale spend." },
        { n: "03", title: "Compound", body: "Systems that get sharper every quarter, not just noisier." },
      ],
      gallery: [IMG.marketingA, IMG.marketingB, IMG.marketingC, IMG.marketingD],
      quote:
        "Growth is a byproduct of clarity. Everything else is drag.",
    },
    graphic: {
      number: "07",
      name: "Graphic Design",
      tagline: "Print, motion,\nand the space\nin between.",
      intro:
        "Graphic design is the discipline that binds everything else together. Print, editorial, packaging, motion, type systems, environmental graphics — every surface where brand meets eye. We work in the tradition of Swiss precision, but with the appetite of a studio that grew up online.",
      paragraphs: [
        "We treat editorial and print as first-class formats: books, catalogs, invitations, packaging. We motion-design our own type. We build packaging that survives the shelf and the unboxing video in equal measure.",
        "For teams already running in-house, we deliver type systems, grid systems and component kits that make their design work sing — long after we've left the room.",
      ],
      capabilities: [
        "Editorial & print design",
        "Packaging & product graphics",
        "Type design & custom lettering",
        "Motion design & animated identities",
        "Environmental & signage graphics",
        "Design systems & templates",
      ],
      approach: [
        { n: "01", title: "Grid", body: "Everything sits on a system. We choose the constraints before we choose the aesthetics." },
        { n: "02", title: "Type", body: "Type is the loudest voice on the page. We treat it that way." },
        { n: "03", title: "Craft", body: "Print samples, motion tests, kerning that could keep us up at night." },
      ],
      gallery: [IMG.graphicA, IMG.graphicB, IMG.graphicC, IMG.graphicD],
      quote:
        "Graphic design is invisible when it's done well. Which is why it takes so long to do it well.",
    },
  },

  it: {
    branding: {
      number: "01",
      name: "Branding",
      tagline: "Sistemi che rendono\ni brand indimenticabili.",
      intro:
        "Un brand non è un logo. È un sistema — un insieme di decisioni, prese in anticipo, che permettono a ogni touchpoint di parlare con la stessa voce. Progettiamo questi sistemi dalle fondamenta: posizionamento strategico, identità visiva e verbale, e i playbook che servono al tuo team per tenerlo vivo dopo la consegna.",
      paragraphs: [
        "Iniziamo dal livello strategico — il perché. A chi si rivolge il brand, contro cosa si posiziona, che promessa fa? Da lì costruiamo l'identità: wordmark, simboli, sistemi tipografici, colore, motion, suono, tono di voce. Tutto pensato per comporsi, non solo per coesistere.",
        "Le consegne vivono in brand book che il tuo team usa davvero — nessun PDF da 200 pagine che non aprirà nessuno. Al loro posto: un sito vivo, asset componibili e un playbook di lancio che parte veloce.",
      ],
      capabilities: [
        "Brand strategy & positioning",
        "Naming & identità verbale",
        "Wordmark & sistemi di simboli",
        "Sistemi tipografici e cromatici",
        "Brand guidelines / brand hub",
        "Strategia di lancio & rollout",
      ],
      approach: [
        { n: "01", title: "Diagnosi", body: "Workshop, audit, teardown competitivi. Mappiamo dove il brand è davvero rispetto a dove deve andare." },
        { n: "02", title: "Progetto", body: "Due direzioni, iterate. Testiamo gli estremi così il sistema finale è scelto — non subito per default." },
        { n: "03", title: "Deploy", body: "Guidelines, template, piano di lancio. Restiamo in stanza per le prime sei settimane." },
      ],
      gallery: [IMG.brandingA, IMG.brandingB, IMG.brandingC, IMG.brandingD],
      quote:
        "I brand che ammiriamo non sono rumorosi — sono coerenti. Ogni superficie conferma la precedente.",
    },
    digital: {
      number: "02",
      name: "Contenuti Digitali",
      tagline: "Contenuti che meritano\nlo scroll.",
      intro:
        "I contenuti digitali sono il punto in cui il brand diventa cultura. Concepiamo, produciamo e dirigiamo campagne, serie editoriali, film e fotografia per i feed in cui il tuo pubblico vive davvero — con una direzione artistica che sopravvive al repost, al crop, alla ricondivisione.",
      paragraphs: [
        "Dai drop di singola immagine alle campagne multi-mercato, disegniamo sistemi di contenuto distintivi e replicabili. Cast, styling, motion, sound, montaggio — ogni layer diretto con la cura di una campagna print.",
        "Costruiamo motori di contenuto interni per brand che devono uscire ogni settimana, e affianchiamo i clienti che vogliono alzare l'asticella sui loro momenti flagship.",
      ],
      capabilities: [
        "Direzione creativa campagne",
        "Serie editoriali",
        "Film & video production",
        "Fashion & product photography",
        "Direzione artistica social",
        "Casting & produzione",
      ],
      approach: [
        { n: "01", title: "Idea", body: "Un concept, così affilato da sopravvivere alla traduzione tra formati e mercati." },
        { n: "02", title: "Craft", body: "Cast, location, luce, suono. Curiamo i dettagli che gli altri saltano." },
        { n: "03", title: "Ship", body: "Cadenza settimanale, review mensili, reset trimestrali. Contenuto come sistema, non come stunt." },
      ],
      gallery: [IMG.digitalA, IMG.digitalB, IMG.digitalC, IMG.digitalD],
      quote:
        "I contenuti migliori non inseguono i trend. Definiscono le regole di quello dopo.",
    },
    interior: {
      number: "03",
      name: "Interior Design",
      tagline: "Spazi che dicono tutto\nprima ancora che tu parli.",
      intro:
        "Progettiamo interni per flagship retail, residenze private e hospitality — spazi dove architettura, brand e comportamento diventano la stessa cosa. Il nostro approccio parte dai materiali e dal programma d'uso: prima di tutto come deve suonare la stanza, poi come renderlo inevitabile.",
      paragraphs: [
        "Ogni progetto inizia in cantiere. Luce, viste, adiacenze. Poi lavoriamo dal basso: palette materica, falegnameria custom, lighting design, wayfinding, integrazione del brand. Il risultato è uno storytelling spaziale che scala — dal singolo flagship al rollout globale.",
        "Collaboriamo con artigiani, artisti e specialisti locali in ogni città in cui costruiamo — perché gli interni migliori portano una traccia di dove sono nati.",
      ],
      capabilities: [
        "Interni retail & flagship store",
        "Architettura residenziale privata",
        "F&B e hospitality",
        "Arredo & falegnameria custom",
        "Lighting design & palette materiche",
        "Rollout & direzione lavori",
      ],
      approach: [
        { n: "01", title: "Luogo", body: "Ogni progetto inizia ascoltando lo spazio — prima dei disegni, prima del mood." },
        { n: "02", title: "Storia", body: "Un brief narrativo che lo spazio deve raccontare — dall'ingresso all'uscita." },
        { n: "03", title: "Cantiere", body: "Prototipi, campioni, mock-up. Preferiamo i materiali ai render." },
      ],
      gallery: [IMG.interiorA, IMG.interiorB, IMG.interiorC, IMG.interiorD],
      quote:
        "L'architettura è branding silenzioso. Agisce sul corpo prima che sulla mente.",
    },
    fashion: {
      number: "04",
      name: "Moda",
      tagline: "Direzione che trasforma\nabiti in linguaggio.",
      intro:
        "La moda è al centro di quello che facciamo. Dirigiamo collezioni, costruiamo narrazioni stagionali, produciamo lookbook e affianchiamo maison su collaborazioni che definiscono un anno. Il nostro team unisce stylist, image-maker e brand strategist come un'unica squadra — perché una collezione non è solo abiti, è un punto di vista.",
      paragraphs: [
        "Dalle maison affermate ai label emergenti, disegniamo direzione di collezione, concept stagionali e le campagne e i lookbook che li portano nel mondo. Casting deliberato. Styling con firma. Film curati come i capi che raccontano.",
        "Guidiamo anche brand collaboration: più il pairing è complesso, più ci interessa. Universi multi-brand fatti con gusto.",
      ],
      capabilities: [
        "Direzione collezione & brief stagionali",
        "Lookbook & imagery di campagna",
        "Styling & direzione artistica",
        "Fashion film",
        "Brand collaboration",
        "Design di sfilata e presentazione",
      ],
      approach: [
        { n: "01", title: "Mood", body: "Reference wall, riferimenti cinematografici, triangolazione culturale. Dove sta la stagione?" },
        { n: "02", title: "Cast", body: "Il casting è direzione. Cerchiamo tra città e community, non solo tra agenzie." },
        { n: "03", title: "Inquadratura", body: "Fotografia e film che reggono anche la stagione dopo — non solo questa." },
      ],
      gallery: [IMG.fashionA, IMG.fashionB, IMG.fashionC, IMG.fashionD],
      quote:
        "Una grande collezione ti dice dove sta andando la cultura, sei mesi prima che ci arrivi.",
    },
    music: {
      number: "05",
      name: "Musica",
      tagline: "Il suono\nche porta il brand.",
      intro:
        "La musica è la nostra arma segreta. Costruiamo identità sonore da zero, componiamo colonne originali per film e campagne, curiamo la supervisione musicale per ambienti brand e retail, e affianchiamo direttamente gli artisti come direzione creativa. Il suono è il modo più rapido per far sentire un brand — lo prendiamo sul serio.",
      paragraphs: [
        "Compositori, mixer e music supervisor interni lavorano fianco a fianco con il team di design fin dal primo giorno — così l'identità sonora non è un accessorio incollato al sistema visivo, ma parte nativa.",
        "Per gli artisti agiamo da direzione creativa estesa: cover, campagna, film, live. Uno studio, un linguaggio.",
      ],
      capabilities: [
        "Sistemi di identità sonora",
        "Colonne originali per film & campagne",
        "Music supervision & licensing",
        "Sound design per retail e ambienti",
        "Direzione creativa d'artista",
        "Design d'album e tour",
      ],
      approach: [
        { n: "01", title: "Ascolto", body: "Mappiamo il territorio sonoro che il brand occupa — e quello che dovrebbe occupare." },
        { n: "02", title: "Composizione", body: "Lavoro originale dai nostri compositori interni, con opzione licensing." },
        { n: "03", title: "Play", body: "Deployment su ogni touchpoint: dal film al negozio all'attesa telefonica." },
      ],
      gallery: [IMG.musicA, IMG.musicB, IMG.musicC, IMG.musicD],
      quote:
        "Dimentichi cosa ha detto un brand. Non dimentichi mai come suonava.",
    },
    marketing: {
      number: "06",
      name: "Marketing",
      tagline: "Crescita\nsenza perdere\nla trama.",
      intro:
        "Aiutiamo i brand ad andare sul mercato senza perdere ciò che li rende meritevoli d'essere sul mercato. Strategia, lanci, paid media, sistemi di crescita, retention — sempre al servizio dell'equity di lungo periodo, mai a sue spese.",
      paragraphs: [
        "La nostra practice di marketing è integrata con il team creativo dal giorno uno. Significa che l'annuncio, la landing, l'email CRM e il momento in negozio condividono lo stesso DNA — perché sono stati progettati allo stesso tavolo.",
        "Costruiamo sistemi di crescita che non richiedono al fondatore di diventare un marketer a tempo pieno. Lanci ritmati, canali misurabili, e la disciplina di dire no alle tattiche che feriranno il brand tra un anno.",
      ],
      capabilities: [
        "Strategia go-to-market",
        "Playbook di lancio",
        "Paid media & performance",
        "CRM & lifecycle marketing",
        "Sperimentazione di crescita",
        "Retention & community",
      ],
      approach: [
        { n: "01", title: "Posizione", body: "Cosa vendi, a chi, contro chi? Il resto è a valle." },
        { n: "02", title: "Prova", body: "Esperimenti piccoli e reali — prima di scalare la spesa." },
        { n: "03", title: "Composto", body: "Sistemi che ogni trimestre diventano più affilati, non solo più rumorosi." },
      ],
      gallery: [IMG.marketingA, IMG.marketingB, IMG.marketingC, IMG.marketingD],
      quote:
        "La crescita è un sottoprodotto della chiarezza. Tutto il resto è attrito.",
    },
    graphic: {
      number: "07",
      name: "Grafica",
      tagline: "Stampa, motion\ne lo spazio\nin mezzo.",
      intro:
        "Il graphic design è la disciplina che lega tutto il resto. Stampa, editoriale, packaging, motion, sistemi tipografici, grafica ambientale — ogni superficie in cui il brand incontra l'occhio. Lavoriamo nella tradizione della precisione svizzera, con l'appetito di uno studio cresciuto online.",
      paragraphs: [
        "Trattiamo editoriale e stampa come formati di prima classe: libri, cataloghi, inviti, packaging. Animiamo i nostri caratteri. Costruiamo packaging che regge sullo scaffale e nel video di unboxing.",
        "Per i team con design interno, consegniamo sistemi tipografici, sistemi di griglia e kit di componenti che fanno cantare il loro lavoro — molto dopo che siamo usciti dalla stanza.",
      ],
      capabilities: [
        "Editoriale & stampa",
        "Packaging & grafica prodotto",
        "Type design & lettering custom",
        "Motion design & identità animate",
        "Grafica ambientale & signage",
        "Design system & template",
      ],
      approach: [
        { n: "01", title: "Griglia", body: "Tutto poggia su un sistema. Scegliamo i vincoli prima dell'estetica." },
        { n: "02", title: "Type", body: "Il carattere è la voce più alta della pagina. Lo trattiamo così." },
        { n: "03", title: "Craft", body: "Campioni stampa, test motion, kerning che potrebbe tenerci svegli la notte." },
      ],
      gallery: [IMG.graphicA, IMG.graphicB, IMG.graphicC, IMG.graphicD],
      quote:
        "Il graphic design è invisibile quando è fatto bene. Ecco perché farlo bene richiede tanto tempo.",
    },
  },
};

export const servicePageUi = {
  en: {
    breadcrumbBack: "Back to home",
    servicesLabel: "Services",
    introEyebrow: "Overview",
    capabilitiesEyebrow: "Capabilities",
    approachEyebrow: "Approach",
    galleryEyebrow: "Selected imagery",
    nextService: "Next discipline",
    ctaTitle: "Have a brief?",
    ctaBody: "Tell us about your project. We reply to every message within 48 hours.",
    ctaButton: "Start a project",
    notFoundTitle: "Discipline not found.",
    notFoundBack: "Return home",
  },
  it: {
    breadcrumbBack: "Torna alla home",
    servicesLabel: "Servizi",
    introEyebrow: "Panoramica",
    capabilitiesEyebrow: "Cosa facciamo",
    approachEyebrow: "Approccio",
    galleryEyebrow: "Immagini selezionate",
    nextService: "Prossima disciplina",
    ctaTitle: "Hai un brief?",
    ctaBody: "Raccontaci del tuo progetto. Rispondiamo a ogni messaggio entro 48 ore.",
    ctaButton: "Inizia un progetto",
    notFoundTitle: "Disciplina non trovata.",
    notFoundBack: "Torna alla home",
  },
};

export const serviceOrder = [
  "branding",
  "digital",
  "interior",
  "fashion",
  "music",
  "marketing",
  "graphic",
];
